import type {
  EndpointSecurityGlobalSettings,
  GeneralInfoSection,
  UserDataGovernanceGlobalSettings,
} from "@/_store/msp/global-policies/msp-global-policies.module.ts";
import type { DevicesSettings } from "@/_store/devices-settings.module.ts";
import type { CloudAppsSettings } from "@/_store/cloud-apps/cloud-apps-settings.module.ts";
import type { PrivacySensitiveDataSettings } from "@/_store/endpoint-data-governance/privacy-sensitive-data.module.ts";
import { Industries } from "@/constants/data-governance.ts";
import type { EmailSecuritySettings } from "@/_store/email-security/email-settings.module.ts";
import {
  QuarantineFolder,
  ScanSensitivityLevels,
  SecurityMode,
  PhishingReportFrequency,
  PhishingReportSelectionType,
} from "@/constants/emails.ts";
import { DevicePostureOsType, DeviceVulnerability } from "@/constants/devices.ts";
import { EdrRemediationMode } from "@/constants/edr.ts";
import type { EdrSettings } from "@/_store/edr-settings.module.ts";
import type { DevicePostureRegularItem } from "@/_store/device-posture/devices-posture-settings.module.ts";
import {
  ProxySensitivityLevel,
  UserDataGovernanceMode,
} from "@/_store/user-data-governance/user-data-governance.module.ts";

export enum ManageWorkspacesContext {
  SINGLE_HIERARCHY = "singleHierarchy",
  GLOBAL_WORKSPACES = "globalWorkspaces",
}

export enum GlobalPolicyAction {
  EDIT = "editGlobalMspPolicy",
  ADD = "addGlobalMspPolicy",
  DUPLICATE = "duplicateGlobalMspPolicy",
  DISCARD = "discardGlobalMspPolicy",
  DELETE = "deleteGlobalMspPolicy",
  UNABLE_TO_DELETE = "unableToDeleteGlobalMspPolicy",
  APPLY = "applyGlobalMspPolicy",
  APPLY_POLICY_CONFIRMATION = "applyGlobalPolicyConfirmation",
  VIEW_DEFAULT = "viewDefaultPolicy",
  RESTORE_GLOBAL_POLICY = "restoreGlobalPolicy",
}

export enum GlobalPolicyModalAction {
  DELETE = "deleteMspGlobalPolicySection",
}

export enum GlobalPolicySection {
  GENERAL_INFO = "generalInfo",
  CLOUD_SECURITY = "cloudSecurity",
  DEVICE_SETTINGS = "devicesSettings",
  ENDPOINT_DATA_GOVERNANCE = "endpointDataGovernance",
  USER_DATA_GOVERNANCE = "userDataGovernance",
  EMAIL_SECURITY = "emailSecurity",
  ENDPOINT_SECURITY = "endpointSecurity",
  EDR = "edrSettings",
}

interface DefaultGlobalPolicies {
  [GlobalPolicySection.GENERAL_INFO]: GeneralInfoSection;
  [GlobalPolicySection.CLOUD_SECURITY]: CloudAppsSettings;
  [GlobalPolicySection.DEVICE_SETTINGS]: DevicesSettings;
  [GlobalPolicySection.ENDPOINT_DATA_GOVERNANCE]: PrivacySensitiveDataSettings;
  [GlobalPolicySection.USER_DATA_GOVERNANCE]: UserDataGovernanceGlobalSettings;
  [GlobalPolicySection.EMAIL_SECURITY]: EmailSecuritySettings;
  [GlobalPolicySection.ENDPOINT_SECURITY]: EndpointSecurityGlobalSettings;
  [GlobalPolicySection.EDR]: EdrSettings;
}

export const defaultEmptyPolicies: DefaultGlobalPolicies = {
  [GlobalPolicySection.GENERAL_INFO]: { name: "", description: "" },
  [GlobalPolicySection.CLOUD_SECURITY]: {
    inactiveDays: 30,
    excludedIpAddresses: [],
    automaticIpExclusionEnabled: false,
  },
  [GlobalPolicySection.DEVICE_SETTINGS]: {
    heartbeatInterval: 7,
    allowToUninstallEnabled: false,
    enableUserBasedAuthentication: false,
    allowToUninstallLabels: [],
    autoUpdateEnabled: false,
    autoUpdateLabels: [],
    visibilityModeEnabled: false,
    visibilityModeLabels: [],
  },
  [GlobalPolicySection.ENDPOINT_DATA_GOVERNANCE]: {
    wizardParameters: {
      industries: [Industries.OTHER],
      isDefault: false,
    },
    monitorNonPublicData: false,
    monitorPersonalData: false,
    monitorHealthData: false,
    monitorCreditCardData: false,
  },
  [GlobalPolicySection.USER_DATA_GOVERNANCE]: {
    monitoring: {
      wizardParameters: {
        industries: [Industries.OTHER],
        isDefault: false,
      },
      monitorNonPublicData: false,
      monitorPersonalData: false,
      monitorHealthData: false,
      monitorCreditCardData: false,
      outboundProxyDlpConfig: {
        outboundProxyBlockingMode: UserDataGovernanceMode.MONITOR,
        outboundProxySensitivityLevel: ProxySensitivityLevel.LOW,
        outboundProxyConfigurationStatus: null,
        hasCloudServicesWithoutOutboundProxy: false,
        outboundProxyBlockingModeAvailable: false,
        isProxyActive: false,
      },
      customData: {
        monitorPasswords: false,
        monitorCertificates: false,
        monitorSourceCodes: false,
        monitorSensitiveKeywords: false,
        sensitiveKeywords: [],
        monitorSensitiveFileTypes: false,
        sensitiveFileTypes: [],
      },
    },
    exclusions: {
      emailSubjectKeywordsEnabled: false,
      emailSubjectKeywords: [],
    },
  },
  [GlobalPolicySection.EMAIL_SECURITY]: {
    securityEnabled: true,
    scanMalware: {
      enabled: true,
      action: SecurityMode.QUARANTINE_EMAIL,
    },
    scanPhishing: {
      enabled: true,
      action: SecurityMode.QUARANTINE_EMAIL,
    },
    scanPromptInjection: {
      enabled: true,
      action: SecurityMode.QUARANTINE_EMAIL,
    },
    scanSpam: {
      enabled: true,
      action: SecurityMode.WARN_RECIPIENT,
    },
    scanNewlyRegisteredDomains: {
      enabled: true,
      action: SecurityMode.WARN_RECIPIENT,
    },
    scanUnverifiedSender: {
      enabled: true,
      action: SecurityMode.WARN_RECIPIENT,
    },
    scanUnknownExternalSender: {
      enabled: true,
      action: SecurityMode.WARN_RECIPIENT,
    },
    scanEncryptedAttachments: {
      enabled: true,
      action: SecurityMode.WARN_RECIPIENT,
    },
    quarantineFileTypesEnabled: false,
    quarantineFileTypes: [
      "exe",
      "dll",
      "bat",
      "vb",
      "js",
      "com",
      "iso",
      "chm",
      "lnk",
      "htm",
      "html",
      "bin",
      "pif",
      "msi",
      "sh",
      "py",
      "vbs",
      "scr",
      "hta",
      "cmd",
      "msc",
      "vbe",
      "jse",
      "scf",
      "inf",
      "reg",
      "ps",
      "ps1",
      "psc1",
      "ps1xml",
      "ps2",
      "psc2",
      "ps2xml",
      "ws",
      "wsf",
      "wsc",
      "wsh",
      "msh",
      "mshxml",
      "msh1",
      "msh1xml",
      "msh2",
      "msh2xml",
      "msp",
      "cpl",
      "gadget",
    ],
    quarantineCustomFileTypes: [],
    quarantineFolder: QuarantineFolder.SUSPECTED,
    expirationEnabled: false,
    expirationCountDays: 0,
    restrictForwarding: false,
    exclusionSecuritySettingsDto: {
      emailSubjectKeywordsEnabled: false,
      emailSubjectKeywords: [],
    },
    securityAwarenessConfigDto: {
      enabled: false,
      providerConfigurations: [],
    },
    scanSensitivity: ScanSensitivityLevels.HIGH,
    adminEmail: "",
    blockedEmailReportsConfig: {
      enabled: false,
      selectionType: PhishingReportSelectionType.ALL,
      frequency: PhishingReportFrequency.DAILY,
      labelsEnabled: false,
      groupsEnabled: false,
      usersEnabled: false,
      labels: [],
      groups: [],
      users: [],
    },
    autoForwardingBlockingPolicy: {
      enabled: false,
      selectionType: PhishingReportSelectionType.ALL,
      labelsEnabled: false,
      groupsEnabled: false,
      usersEnabled: false,
      labels: [],
      groups: [],
      users: [],
    },
  },
  [GlobalPolicySection.ENDPOINT_SECURITY]: {
    addons: {
      wifiPhishingEnabled: false,
      wifiPhishingLabels: [],
    },
    devicePosture: {
      [DevicePostureOsType.WINDOWS]: {
        [DeviceVulnerability.UAC_NOTIFICATIONS_MISSING]: [],
        [DeviceVulnerability.DEVICE_PASSWORD_MISSING]: [],
        [DeviceVulnerability.FIREWALL_DISABLED]: [],
        [DeviceVulnerability.ENCRYPTION_DISABLED]: [],
        [DeviceVulnerability.DEVELOPMENT_MODE_ENABLED]: [],
        [DeviceVulnerability.NON_GENUINE_WINDOWS]: [],
      } as unknown as Record<DeviceVulnerability, DevicePostureRegularItem[]>,
      [DevicePostureOsType.MAC_OS]: {
        [DeviceVulnerability.DEVICE_PASSWORD_MISSING]: [],
        [DeviceVulnerability.FIREWALL_DISABLED]: [],
        [DeviceVulnerability.ENCRYPTION_DISABLED]: [],
        [DeviceVulnerability.GATEKEEPER_UNAVAILABLE]: [],
        [DeviceVulnerability.APPLE_MOBILE_FILE_INTEGRITY_UNAVAILABLE]: [],
        [DeviceVulnerability.SYSTEM_INTEGRITY_PROTECTION_UNAVAILABLE]: [],
      } as unknown as Record<DeviceVulnerability, DevicePostureRegularItem[]>,
      usbLockdownSettings: [],
      deviceWiFiSettings: {
        [DevicePostureOsType.WINDOWS]: [],
        [DevicePostureOsType.MAC_OS]: [],
      },
      userAccountPolicy: [],
    },
    ngav: {
      realTimeMalwareProtectionEnabled: false,
      atcEnabled: false,
      atcLabels: [],
      vssPolicyEnabled: false,
      vssPolicyLabels: [],
      sharedOnAccessCooperationEnabled: false,
      sharedOnAccessCooperationLabels: [],
      initialDevicesScanEnabled: false,
      initialDevicesScanLabels: [],
      enforceContainerQuarantineEnabled: false,
      enforceContainerQuarantineLabels: [],
    },
  },
  [GlobalPolicySection.EDR]: {
    enableEdrEnforcement: true,
    remediationMode: EdrRemediationMode.DETECT_ONLY,
    labels: [],
  },
};

export enum MspUsersAndDevicesTrendExportOption {
  BOTH = "both",
  USERS = "users",
  DEVICES = "devices",
}

export enum MspTreeviewSelectionAction {
  SELECT_ALL_DESCENDANTS = "selectAllDescendants",
  SELECT_ALL_CHILD_WORKSPACES = "selectAllChildWorkspaces",
}
